import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { HelperProfile } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Briefcase, DollarSign, Globe, Clock, ShieldCheck, CalendarDays } from 'lucide-react';
import { useDataDictionaries, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictValueByCode } from '../utils/i18n';
import { formatRelativeTime, latestDate, parseServerDate, calculateAge } from '../utils/date';

interface HelperCardProps {
  helper: HelperProfile;
  onViewDetail?: (helper: HelperProfile) => void;
  onBookmark?: (helper: HelperProfile) => void;
  isBookmarked?: boolean;
  isViewed?: boolean;
  matchPercent?: number;
}

const HelperCard: React.FC<HelperCardProps> = ({
  helper,
  onBookmark,
  isBookmarked = false,
  isViewed = false,
  matchPercent,
}) => {
  const { t } = useTranslation();
  const { dictionariesByParent } = useDataDictionaries({
    parentCodes: [
      DATA_DICTIONARY_TYPES.OTHER_SKILLS,
      DATA_DICTIONARY_TYPES.COOKING_SKILLS,
      DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
      DATA_DICTIONARY_TYPES.HOUSEWORK,
      DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
      DATA_DICTIONARY_TYPES.NATIONALITY,
    ],
  });
  const allSkillDicts = [
    ...(dictionariesByParent.get(DATA_DICTIONARY_TYPES.OTHER_SKILLS) ?? []),
    ...(dictionariesByParent.get(DATA_DICTIONARY_TYPES.COOKING_SKILLS) ?? []),
    ...(dictionariesByParent.get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE) ?? []),
    ...(dictionariesByParent.get(DATA_DICTIONARY_TYPES.HOUSEWORK) ?? []),
  ];
  const langDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS) ?? [];
  const nationalityDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.NATIONALITY) ?? [];

  const formatSalary = (salary: number, currency?: string) => {
    const currencySymbol = currency === 'HKD' ? 'HK$' : currency === 'USD' ? '$' : '';
    return `${currencySymbol}${salary.toLocaleString()}`;
  };

  // 獲取技能標籤（從 skills 或 cookingSkills）
  const getSkillTags = () => {
    const tags: string[] = [];
    if (helper.skills) {
      const skillsArr: string[] = Array.isArray(helper.skills)
        ? (helper.skills as string[])
        : String(helper.skills).split(',');
      tags.push(...skillsArr.slice(0, 3));
    }
    if (helper.cookingSkills && helper.cookingSkills.length > 0) {
      const cookingSkill = helper.cookingSkills[0];
      const skillName = typeof cookingSkill === 'string' ? cookingSkill : String(cookingSkill);
      if (!tags.includes(skillName)) {
        tags.push(skillName);
      }
    }
    return tags;
  };

  const skillTags = getSkillTags();

  return (
    <Card className="overflow-hidden group hover:shadow-lg transition-shadow border-border h-full flex flex-col">
      <div className="relative aspect-[4/3] bg-muted overflow-hidden">
        {helper.avatarUrl ? (
          <img
            src={helper.avatarUrl}
            alt={helper.fullName}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-primary/10 text-primary font-bold text-4xl">
            {helper.fullName?.charAt(0)?.toUpperCase()}
          </div>
        )}
        
        <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1">
          {matchPercent !== undefined && matchPercent >= 93 && (
            <Badge className="bg-amber-500 text-white border-0 text-[11px] shadow">⚡ {t('common.highMatch', '高匹配')}</Badge>
          )}
          {isViewed && (
            <Badge variant="outline" className="bg-background/80 backdrop-blur-sm text-[11px] shadow border-border text-muted-foreground">{t('common.viewed', '已瀏覽')}</Badge>
          )}
          {onBookmark && (
            <button
              onClick={(e) => {
                e.preventDefault();
                onBookmark(helper);
              }}
              className="p-2 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background transition-colors shadow-sm border border-border"
            >
              <Heart
                className={`h-5 w-5 ${
                  isBookmarked ? "fill-red-500 text-red-500" : "text-muted-foreground"
                }`}
              />
            </button>
          )}
        </div>
        
        <Badge className="absolute bottom-3 left-3 bg-secondary text-secondary-foreground shadow-sm text-[12px]">
          {helper.nationality ? getDictValueByCode(nationalityDicts, helper.nationality) : t('helperCard.unknownNationality')}
        </Badge>
      </div>
      <CardContent className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <RouterLink to={`/helper/${helper.id}`}>
            <h3 className="font-semibold text-lg group-hover:text-primary transition-colors line-clamp-1">
              {helper.fullName}
            </h3>
          </RouterLink>
          {(helper.verifiedDocCount ?? 0) > 0 && (
            <Badge className="shrink-0 text-white border-0 gap-1 text-[11px]" style={{backgroundColor:'#2a6652'}}>
              <ShieldCheck className="h-3 w-3" />
              {t('helperCard.verified', '已認證')} {helper.verifiedDocCount}
            </Badge>
          )}
        </div>

        {latestDate(helper.lastLoginAt, helper.updatedAt) && (
          <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {(() => {
              const d = latestDate(helper.lastLoginAt, helper.updatedAt);
              if (!d) return null;
              const ms = parseServerDate(d).getTime();
              if (isNaN(ms)) return null;
              const mins = Math.floor((Date.now() - ms) / 60000);
              const hrs = Math.floor(mins / 60);
              const days = Math.floor(hrs / 24);
              if (mins < 1) return t('helperCard.activeJustNow', '剛剛活躍');
              if (mins < 60) return t('helperCard.activeMinsAgo', '{{n}} 分鐘前活躍', { n: mins });
              if (hrs < 24) return t('helperCard.activeHrsAgo', '{{n}} 小時前活躍', { n: hrs });
              if (days < 30) return t('helperCard.activeDaysAgo', '{{n}} 天前活躍', { n: days });
              return t('helperCard.lastActive', '{{time}}活躍', { time: formatRelativeTime(d) });
            })()}
          </p>
        )}

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-muted-foreground mb-2">
          {helper.dateOfBirth && (
            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 shrink-0" />
              <span>{calculateAge(helper.dateOfBirth)} {t('helperCard.yearsOld', '歲')}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1">{helper.experienceYears} {t('helperCard.yearsUnit', '年經驗')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <DollarSign className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1">
              {helper.expectedSalary ? `${formatSalary(helper.expectedSalary, helper.expectedSalaryCurrency)}/${t('helperCard.perMonth', '月')}` : t('common.negotiable')}
            </span>
          </div>
          {helper.languageSkills && (
            <div className="flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-1">
                {((Array.isArray(helper.languageSkills)
                  ? (helper.languageSkills as string[])
                  : String(helper.languageSkills).split(',')
                ) as string[]).map(l => getDictValueByCode(langDicts, l.trim().split(':')[0])).filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>

        {helper.lookingForJob && (
          <div className="flex items-center gap-1.5 text-sm text-green-600 mb-1">
            <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
            <span>{t('helperCard.availableNow', '即時可開工')}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-4 min-h-[24px]">
          {skillTags.slice(0, 3).map((tag, index) => (
            <Badge key={index} variant="outline" className="text-[12px] py-0 px-2 h-5 font-normal">
              {getDictValueByCode(allSkillDicts, tag.trim())}
            </Badge>
          ))}
          {skillTags.length > 3 && (
            <Badge variant="outline" className="text-[12px] py-0 px-2 h-5 font-normal">
              +{skillTags.length - 3}
            </Badge>
          )}
        </div>
        <div className="flex-1" />
        <Button asChild className="w-full mt-4 bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm">
          <RouterLink to={`/helper/${helper.id}`}>
            {t('helperCard.viewDetails', '查看詳情')}
          </RouterLink>
        </Button>
      </CardContent>
    </Card>
  );
};

export default HelperCard;
